/**
 * admin/src/hooks/useAnalytics.ts
 * Hooks cho Analytics page
 *
 * useAnalyticsDaily    — time-series hoạt động ngày
 * useAnalyticsGrowth   — tăng trưởng user theo tuần
 * useAnalyticsTop      — leaderboard top students
 * useAnalyticsRevenue  — doanh thu theo tháng
 * useAnalyticsAll      — load song song cả 4 endpoints
 */

import { useState, useEffect } from 'react';
import { adminFetch } from './useAdmin';
import type {
  AnalyticsDailyPoint,
  AnalyticsGrowthPoint,
  AnalyticsTopStudent,
  AnalyticsRevenuePoint,
} from '../types/admin';

// ─── Individual hooks ─────────────────────────────────────────────────────────

export function useAnalyticsDaily(days = 30) {
  const [data, setData] = useState<AnalyticsDailyPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    adminFetch<{ days: number; data: AnalyticsDailyPoint[] }>('/analytics/daily', {
      params: { days },
    })
      .then((res) => { if (!cancelled) { setData(res.data ?? []); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi analytics/daily');
          setIsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [days]);

  return { data, isLoading, error };
}

export function useAnalyticsGrowth() {
  const [data, setData] = useState<AnalyticsGrowthPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    adminFetch<{ data: AnalyticsGrowthPoint[] }>('/analytics/growth')
      .then((res) => { if (!cancelled) { setData(res.data ?? []); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi analytics/growth');
          setIsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  return { data, isLoading, error };
}

export type TopStudentSort = 'total_stars' | 'current_streak' | 'longest_streak' | 'total_sessions';

export function useAnalyticsTop(by: TopStudentSort = 'total_stars') {
  const [data, setData] = useState<AnalyticsTopStudent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    adminFetch<{ by: string; data: AnalyticsTopStudent[] }>('/analytics/top', {
      params: { by },
    })
      .then((res) => { if (!cancelled) { setData(res.data ?? []); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi analytics/top');
          setIsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [by]);

  return { data, isLoading, error };
}

export function useAnalyticsRevenue() {
  const [data, setData] = useState<AnalyticsRevenuePoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    adminFetch<{ data: AnalyticsRevenuePoint[] }>('/analytics/revenue')
      .then((res) => { if (!cancelled) { setData(res.data ?? []); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi analytics/revenue');
          setIsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  return { data, isLoading, error };
}

// ─── Composite hook — load cả 4 song song ─────────────────────────────────────
export function useAnalyticsAll(days = 30) {
  const daily   = useAnalyticsDaily(days);
  const growth  = useAnalyticsGrowth();
  const top     = useAnalyticsTop('total_stars');
  const revenue = useAnalyticsRevenue();

  const isLoading = daily.isLoading || growth.isLoading || top.isLoading || revenue.isLoading;

  return { daily, growth, top, revenue, isLoading };
}
